"use client";

import { Suspense } from "react";
import { usePathname } from "next/navigation";
import { AppHeader } from "@/components/ui/organisms/app-header";
import { StudioPage } from "@/components/studio-page";
import { HandheldNotice } from "@/components/studio/handheld-notice";
import { VirtuosaLayoutHelper } from "@/components/ui/organisms/virtuosa-layout-helper";
import { HintProvider } from "@/contexts/hint-context";
import { LiveSessionProvider } from "@/contexts/live-session-context";
import { ReplayProvider } from "@/contexts/replay-context";
import { WhatsNewProvider } from "@/contexts/whats-new-context";
import { useWakeLock } from "@/hooks/use-wake-lock";
import { isStudioPathname } from "@/lib/studio/studio-routes";

/**
 * La coque des pages principales : le header, le studio monté en permanence,
 * et les autres routes par-dessus.
 *
 * Client, parce qu'elle lit le chemin. Ce qui ne se décide que côté serveur —
 * une variable d'environnement lue à l'exécution — lui arrive en props depuis
 * `app/(main)/layout.tsx`.
 */
export function MainLayoutShell({
  children,
  noteDust,
}: {
  children: React.ReactNode;
  /** Les notes partent en paillettes en passant la tête de lecture (`NOTE_DUST`). */
  noteDust: boolean;
}) {
  const pathname = usePathname();
  // Every studio view (`/studio`, `/studio/harmony`, …) is served by the
  // permanently-mounted `StudioPage` below, its routes render `null`.
  const isStudio = isStudioPathname(pathname);

  // Le verrou couvre toute la présence dans le studio, et non la seule lecture
  // en cours : sur le pupitre, le moment où l'écran s'éteint est justement
  // celui où le transport est à l'arrêt pendant qu'on retravaille un passage
  // au piano. L'indexer sur la lecture le relâcherait au pire instant.
  useWakeLock(isStudio);

  return (
    <HintProvider>
      <WhatsNewProvider>
        {/*
          Zones sûres pour tout ce qui est **dans le flux** (variables définies
          dans `globals.css`) : cette coque porte le header et le studio, la
          padder ici fait descendre le header sous l'encoche et remonter le
          clavier au-dessus de la barre d'accueil. Le canvas Pixi se réajuste
          seul via son ResizeObserver. Sur desktop les variables valent 0px.

          Ça ne couvre pas les descendants en `position: fixed` — modales,
          carte de bienvenue, popovers en portail — qui se calent sur le
          viewport et non sur cet ancêtre paddé, et repassent donc sous
          l'encoche. Ceux-là portent la classe `inset-safe` (voir
          `globals.css`) au lieu d'`inset-0`.
        */}
        <div className="relative w-full h-full flex flex-col bg-bg-deep overflow-hidden pt-[var(--safe-top)] pr-[var(--safe-right)] pb-[var(--safe-bottom)] pl-[var(--safe-left)]">
          <AppHeader />
        {/*
          `StudioPage` owns the area below the header and is mounted
          permanently (the `/studio*` routes themselves render `null`) to
          avoid remounting the piano-roll / re-downloading samples on
          navigation, including when switching studio views.
          The piece tutorial is now a Studio *state* (learn mode), rendered
          by `StudioPage` itself, so no route needs to overlay it anymore.
          On the other routes (`/learn` programs, `/library`, …) `children`
          stacks on top classically. C'est précisément pour ça que la
          bibliothèque n'est pas une vue `/studio/*` : celles-là ne rendent
          aucun `children` et sont entièrement recouvertes en portrait sur
          téléphone.
        */}
          <div className="relative flex-1 flex flex-col min-h-0">
            {/*
              `StudioPage` lit les paramètres d'URL (`useSearchParams`). Sans
              cette frontière, il fait sortir du prérendu **toute** page servie
              par cette coque, y compris celles qui n'ont rien à voir avec le
              studio : le build échouait sur la première page statique
              rencontrée, `/confidentialite` ou `/learn` selon l'ordre.

              Le repli est vide et non un squelette : le studio est déjà monté
              en permanence pour ne pas recharger les samples, et lui donner un
              placeholder ferait clignoter une zone que personne ne regarde
              pendant le rendu des routes hors studio.
            */}
            {/*
              Le fournisseur live est *dans* la frontière `Suspense` : comme
              `StudioPage`, il lit les paramètres d'URL (`?live=`), ce qui sort
              du prérendu tout ce qui l'entoure. Il enveloppe le studio et rien
              d'autre — c'est là que vit le piano qu'il branche, et là que se
              trouve le bouton qui l'ouvre.
            */}
            <Suspense fallback={null}>
              <LiveSessionProvider>
                {/*
                  Le lecteur de replay, sous le fournisseur du direct et autour
                  du studio : il lit `?replay=` comme l'autre lit `?live=`, et
                  il monte une séance de studio de la même façon — avec des
                  fichiers pour source.
                */}
                <ReplayProvider>
                  <StudioPage noteDust={noteDust} />
                {/*
                  Sur téléphone, dans les deux orientations, le studio est
                  injouable (clavier réduit à une octave et demie debout,
                  150 px de course aux notes couché). Le calque le recouvre —
                  toolbar comprise — sans jamais démonter `StudioPage` : c'est
                  un frère de rendu, pas une branche de ternaire, donc les
                  samples restent chargés et la lecture en cours n'est pas
                  coupée. Il s'arrête sous le header, qui reste le chemin de
                  sortie vers `/learn` ou la bibliothèque.

                  Sous le fournisseur live, et non à côté du studio : le calque
                  lit le billet pour s'effacer devant le spectateur d'un
                  récital, qui regarde le rouleau depuis son téléphone. Il
                  partage donc la frontière `Suspense` du studio — et apparaît
                  au même rendu que lui, ce qui ne laisse aucune image du
                  studio découvert. Positionné sur le même ancêtre `relative`
                  qu'avant : ni le fournisseur ni `Suspense` ne posent de nœud.
                */}
                  {isStudio && <HandheldNotice />}
                </ReplayProvider>
              </LiveSessionProvider>
            </Suspense>
            {!isStudio && children}
          </div>
          <VirtuosaLayoutHelper />
        </div>
      </WhatsNewProvider>
    </HintProvider>
  );
}
